import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { RootState } from 'redux/rootReducer'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Demographic } from 'typings/demographic'
import AccountInfo from 'ui/atoms/AccountInfo'
import { clientInfo } from 'configureBrand'
import SwitchAccount from 'ui/organisms/SwitchAccount'
import { setActiveAccount } from 'redux/demographic/demographicSlice'
import images from 'const/images'
import { userAccountInfo } from 'helpers/user'
import { getCardImage } from 'helpers/brandHelpers'
import i18n from 'lang/i18n'

import {
  AccountDetails,
  AccountWrapper,
  AddressDetails,
  CardContainer,
  CardImage,
  SwitchAccountButton,
  SwitchAccountMobileIcon,
  SwitchCardIcon,
  WelcomePanelContainer
} from './subcomponents'
import { getFullAddress } from './helpers'

const { clientName } = clientInfo
const { cardSwap } = images

interface WelcomePanelProps {
  compact?: boolean
}

const WelcomePanel = ({ compact }: WelcomePanelProps): ReactElement => {
  const dispatch = useDispatch()
  const {
    accountSummary: {
      response: {
        accountInfo: { accountLast4 }
      }
    },
    welcomePanel: { demographyInfo, activeAccountInfo },
    loginInfo: { isSwitchLinkedAccountFlow }
  } = useSelector(
    ({ accountSummary, welcomePanel, loginInfo }: RootState) => ({
      accountSummary,
      welcomePanel,
      loginInfo
    }),
    shallowEqual
  )

  const {
    maskedAccount,
    addressLine2,
    city,
    postalCode,
    companyName,
    state: stateName
  } = useMemo(() => getFullAddress(activeAccountInfo), [activeAccountInfo])

  const { isProx } = userAccountInfo

  const welcomeCardImage = useMemo(
    () => getCardImage(clientName),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSwitchLinkedAccountFlow]
  )

  const [isSwitchAccount, setIsSwitchAccount] = useState<boolean>(false)
  const [isMobile, setIsMobile] = useState<boolean>(false)
  const switchAccountFocusableRef = useRef<HTMLButtonElement>(null)
  const switchAccountMobileFocusableRef = useRef<HTMLButtonElement>(null)

  const isSwitchAccountEligible = demographyInfo.some(
    ({ relationship }: Demographic) => relationship?.toUpperCase() === 'PARENT'
  )

  const handleActiveAccountInfo = (): void => {
    if (demographyInfo && demographyInfo.length === 1) {
      const { accountNumber, accountID } = activeAccountInfo
      dispatch(
        setActiveAccount({
          ...demographyInfo[0],
          accountNumber,
          accountID
        })
      )
      if (!isProx) {
        const updatedDemographics = {
          ...demographyInfo[0],
          lastFourDigitAccountNumber: accountLast4
        }
        dispatch(
          setActiveAccount({
            ...updatedDemographics,
            accountNumber,
            accountID
          })
        )
      }
    } else if (demographyInfo && demographyInfo.length > 1) {
      const accountInfo = demographyInfo.find(
        ({ relationship }: Demographic) =>
          relationship === 'PARENT' || relationship === 'Standalone'
      )
      dispatch(setActiveAccount({ ...accountInfo }))
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleActiveAccountInfo, [demographyInfo, accountLast4])

  const handleSwitchAccountClick = (deviceType?: string): void => {
    setIsSwitchAccount((prevState: boolean) => !prevState)
    if (deviceType === 'mobile') {
      setIsMobile(true)
    } else {
      setIsMobile(false)
    }
  }

  return (
    <>
      <WelcomePanelContainer>
        <AccountWrapper>
          <CardContainer>
            <CardImage alt="" src={welcomeCardImage} aria-hidden />
          </CardContainer>
          <AccountDetails>
            <AccountInfo
              lastfourDigitAccountNumber={maskedAccount}
              companyName={companyName}
            />
            {compact ? null : (
              <AddressDetails
                address2={addressLine2}
                city={city}
                state={stateName}
                postalCode={postalCode}
              />
            )}
          </AccountDetails>
        </AccountWrapper>
        {isSwitchAccountEligible && (
          <>
            <SwitchAccountButton
              buttonType="text"
              onClick={() => handleSwitchAccountClick('desktop')}
              ref={switchAccountFocusableRef}
            >
              {i18n({ button: 'switchAccount' })}
            </SwitchAccountButton>
            <SwitchAccountMobileIcon
              aria-label={i18n({ button: 'switchAccount' }) as string}
              onClick={() => handleSwitchAccountClick('mobile')}
              ref={switchAccountMobileFocusableRef}
            >
              <SwitchCardIcon src={cardSwap} alt="" />
            </SwitchAccountMobileIcon>
          </>
        )}
      </WelcomePanelContainer>
      <SwitchAccount
        switchAccountClick={handleSwitchAccountClick}
        isSwitchAccount={isSwitchAccount}
        closeModal={(): void => {
          setIsSwitchAccount(false)
          if (isMobile) {
            switchAccountMobileFocusableRef.current.focus()
          } else {
            switchAccountFocusableRef.current.focus()
          }
        }}
      />
    </>
  )
}

export default WelcomePanel
